import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { 
  Scissors, 
  Calendar, 
  MessageSquare, 
  Star,
  DollarSign,
  Settings,
  LogOut,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  XCircle,
  Upload,
  Plus,
  Trash2,
  Send,
  Eye,
  FileText,
  Shield,
  Milestone,
  AlertTriangle,
  CalendarDays,
  Truck
} from 'lucide-react';
import axios from 'axios';
import MilestoneTracker from '../../components/services/MilestoneTracker';
import DisputeModal from '../../components/services/DisputeModal';
import ProviderCalendarSetup from '../../components/services/ProviderCalendarSetup';
import ProviderDeliveryManagement from '../../components/services/ProviderDeliveryManagement';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Verification status badges
const STATUS_BADGES = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  under_review: { label: 'Under Review', color: 'bg-blue-100 text-blue-700' },
  approved: { label: 'Approved', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
};

// Booking status badges
const BOOKING_STATUS = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: Clock },
  confirmed: { label: 'Confirmed', color: 'bg-blue-100 text-blue-700', icon: CheckCircle2 },
  in_progress: { label: 'In Progress', color: 'bg-purple-100 text-purple-700', icon: Scissors },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: CheckCircle2 },
  cancelled: { label: 'Cancelled', color: 'bg-gray-100 text-gray-700', icon: XCircle },
};

const ProviderDashboard = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [disputes, setDisputes] = useState([]);
  
  // Modal states
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [quoteResponse, setQuoteResponse] = useState({ price: '', timeline: '', message: '' });
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [newService, setNewService] = useState({ name: '', description: '', price: '', duration: '' });
  const [uploading, setUploading] = useState(false);
  
  // Milestone & Dispute states
  const [selectedQuoteForMilestone, setSelectedQuoteForMilestone] = useState(null);
  const [showMilestoneModal, setShowMilestoneModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [disputeBookingId, setDisputeBookingId] = useState(null);
  const [disputeQuoteId, setDisputeQuoteId] = useState(null);

  const providerId = localStorage.getItem('provider_id');
  const providerName = localStorage.getItem('provider_name');

  useEffect(() => {
    if (!providerId) {
      navigate('/provider/login');
      return;
    }
    fetchDashboardData();
  }, [providerId, navigate]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [dashRes, bookingsRes, quotesRes, reviewsRes, disputesRes] = await Promise.all([
        axios.get(`${API_URL}/api/provider/dashboard/${providerId}`),
        axios.get(`${API_URL}/api/provider/bookings/${providerId}`),
        axios.get(`${API_URL}/api/provider/quotes/${providerId}`),
        axios.get(`${API_URL}/api/provider/reviews/${providerId}`),
        axios.get(`${API_URL}/api/services/provider/${providerId}/disputes`),
      ]);
      
      setDashboardData(dashRes.data);
      setBookings(bookingsRes.data.bookings || []);
      setQuotes(quotesRes.data.quotes || []);
      setReviews(reviewsRes.data.reviews || []);
      setDisputes(disputesRes.data.disputes || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('provider_id');
    localStorage.removeItem('provider_user_id');
    localStorage.removeItem('provider_email');
    localStorage.removeItem('provider_name');
    localStorage.removeItem('provider_status');
    navigate('/provider/login');
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.put(`${API_URL}/api/provider/bookings/${bookingId}/status`, { status });
      await fetchDashboardData();
    } catch (error) {
      console.error('Failed to update booking:', error);
    }
  };

  const respondToQuote = async () => {
    if (!selectedQuote || !quoteResponse.price || !quoteResponse.timeline) return;
    
    try {
      await axios.post(`${API_URL}/api/provider/quotes/${selectedQuote.id}/respond`, {
        quote_id: selectedQuote.id,
        quoted_price: parseFloat(quoteResponse.price),
        quoted_timeline: quoteResponse.timeline,
        message: quoteResponse.message,
      });
      
      setShowQuoteModal(false);
      setSelectedQuote(null);
      setQuoteResponse({ price: '', timeline: '', message: '' });
      await fetchDashboardData();
    } catch (error) {
      console.error('Failed to respond to quote:', error);
    }
  };

  const addService = async () => {
    if (!newService.name || !newService.price) return;
    
    try {
      await axios.post(`${API_URL}/api/provider/services/${providerId}/fixed`, {
        name: newService.name,
        description: newService.description,
        price: parseFloat(newService.price),
        duration: newService.duration,
      });
      
      setShowServiceModal(false);
      setNewService({ name: '', description: '', price: '', duration: '' });
      await fetchDashboardData();
    } catch (error) {
      console.error('Failed to add service:', error);
    }
  };

  const removeService = async (serviceId) => {
    try {
      await axios.delete(`${API_URL}/api/provider/services/${providerId}/fixed/${serviceId}`);
      await fetchDashboardData();
    } catch (error) {
      console.error('Failed to remove service:', error);
    }
  };

  const uploadDocument = async (e, docType) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_type', docType);
      
      await axios.post(`${API_URL}/api/provider/profile/${providerId}/documents`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      
      await fetchDashboardData();
    } catch (error) {
      console.error('Failed to upload document:', error);
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-purple-600" />
        <span className="ml-2">Loading dashboard...</span>
      </div>
    );
  }

  const provider = dashboardData?.provider;
  const stats = dashboardData?.stats;
  const verificationStatus = provider?.verification_status || 'pending';
  const statusBadge = STATUS_BADGES[verificationStatus];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="provider-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Scissors className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">{provider?.business_name || 'Provider Dashboard'}</h1>
                <p className="text-sm text-white/80">{providerName}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Badge className={statusBadge?.color}>
                {statusBadge?.label}
              </Badge>
              <Button 
                variant="ghost" 
                className="text-white hover:bg-white/20"
                onClick={handleLogout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Verification Banner */}
      {verificationStatus !== 'approved' && (
        <div className={`${
          verificationStatus === 'rejected' ? 'bg-red-50 border-red-200' : 'bg-yellow-50 border-yellow-200'
        } border-b px-4 py-3`}>
          <div className="max-w-7xl mx-auto flex items-center gap-3">
            <AlertCircle className={`h-5 w-5 ${
              verificationStatus === 'rejected' ? 'text-red-600' : 'text-yellow-600'
            }`} />
            <span className={`text-sm ${
              verificationStatus === 'rejected' ? 'text-red-700' : 'text-yellow-700'
            }`}>
              {verificationStatus === 'pending' && 'Please upload verification documents to get your account approved.'}
              {verificationStatus === 'under_review' && 'Your documents are being reviewed. This usually takes 24-48 hours.'}
              {verificationStatus === 'rejected' && `Your verification was rejected. Reason: ${provider?.verification_notes || 'Please contact support.'}`}
            </span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Calendar className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.bookings?.total || 0}</p>
                  <p className="text-xs text-gray-500">Total Bookings</p>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {stats?.bookings?.pending || 0} pending
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <MessageSquare className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.quotes?.total || 0}</p>
                  <p className="text-xs text-gray-500">Quote Requests</p>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {stats?.quotes?.pending || 0} pending response
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-yellow-100">
                  <Star className="h-5 w-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">{stats?.reviews?.average_rating?.toFixed(1) || '0.0'}</p>
                  <p className="text-xs text-gray-500">Average Rating</p>
                </div>
              </div>
              <div className="mt-2 text-xs text-gray-500">
                {stats?.reviews?.total || 0} reviews
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-green-100">
                  <DollarSign className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold">
                    {stats?.earnings?.currency} {stats?.earnings?.total?.toLocaleString() || 0}
                  </p>
                  <p className="text-xs text-gray-500">Total Earnings</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-8 w-full max-w-5xl mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="bookings">Bookings</TabsTrigger>
            <TabsTrigger value="quotes">Quotes</TabsTrigger>
            <TabsTrigger value="milestones">Milestones</TabsTrigger>
            <TabsTrigger value="delivery">
              <Truck className="h-3 w-3 mr-1" />
              Delivery
            </TabsTrigger>
            <TabsTrigger value="calendar">
              <CalendarDays className="h-3 w-3 mr-1" />
              Calendar
            </TabsTrigger>
            <TabsTrigger value="services">Services</TabsTrigger>
            <TabsTrigger value="verification">Verification</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Recent Bookings */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Bookings</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData?.recent_bookings?.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No bookings yet</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData?.recent_bookings?.slice(0, 3).map((booking) => {
                        const status = BOOKING_STATUS[booking.status];
                        const Icon = status?.icon || Clock;
                        return (
                          <div key={booking.id} className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                              <p className="font-medium text-sm">{booking.service_name}</p>
                              <p className="text-xs text-gray-500">{booking.customer_name}</p>
                            </div>
                            <Badge className={status?.color}>
                              <Icon className="h-3 w-3 mr-1" />
                              {status?.label}
                            </Badge>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Pending Quotes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Pending Quote Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  {dashboardData?.pending_quotes?.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No pending quotes</p>
                  ) : (
                    <div className="space-y-3">
                      {dashboardData?.pending_quotes?.slice(0, 3).map((quote) => (
                        <div key={quote.id} className="p-3 border rounded-lg">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-medium text-sm">{quote.work_type}</p>
                              <p className="text-xs text-gray-500">{quote.customer_name}</p>
                            </div>
                            <Button 
                              size="sm"
                              onClick={() => {
                                setSelectedQuote(quote);
                                setShowQuoteModal(true);
                              }}
                            >
                              Respond
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Bookings Tab */}
          <TabsContent value="bookings">
            <Card>
              <CardHeader>
                <CardTitle>All Bookings</CardTitle>
              </CardHeader>
              <CardContent>
                {bookings.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No bookings yet</p>
                ) : (
                  <div className="space-y-3">
                    {bookings.map((booking) => {
                      const status = BOOKING_STATUS[booking.status];
                      const Icon = status?.icon || Clock;
                      return (
                        <div key={booking.id} className="p-4 border rounded-lg">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{booking.service_name}</p>
                              <p className="text-sm text-gray-500">{booking.customer_name} • {booking.customer_phone}</p>
                              <p className="text-sm text-gray-500 mt-1">
                                {booking.preferred_date && new Date(booking.preferred_date).toLocaleDateString()} at {booking.preferred_time}
                              </p>
                              {booking.notes && (
                                <p className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                                  Note: {booking.notes}
                                </p>
                              )}
                            </div>
                            <div className="text-right">
                              <Badge className={status?.color}>
                                <Icon className="h-3 w-3 mr-1" />
                                {status?.label}
                              </Badge>
                              <p className="font-bold text-purple-600 mt-2">
                                {provider?.currency} {booking.total_amount?.toLocaleString()}
                              </p>
                            </div>
                          </div>
                          
                          {booking.status === 'pending' && (
                            <div className="flex gap-2 mt-4 pt-4 border-t">
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => updateBookingStatus(booking.id, 'confirmed')}
                              >
                                <CheckCircle2 className="h-4 w-4 mr-1" />
                                Accept
                              </Button>
                              <Button 
                                size="sm"
                                variant="destructive"
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                              >
                                <XCircle className="h-4 w-4 mr-1" />
                                Decline
                              </Button>
                            </div>
                          )}
                          
                          {booking.status === 'confirmed' && (
                            <div className="flex gap-2 mt-4 pt-4 border-t">
                              <Button 
                                size="sm"
                                onClick={() => updateBookingStatus(booking.id, 'in_progress')}
                              >
                                Start Work
                              </Button>
                            </div>
                          )}
                          
                          {booking.status === 'in_progress' && (
                            <div className="flex gap-2 mt-4 pt-4 border-t">
                              <Button 
                                size="sm"
                                className="bg-green-600 hover:bg-green-700"
                                onClick={() => updateBookingStatus(booking.id, 'completed')}
                              >
                                Mark Completed
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Quotes Tab */}
          <TabsContent value="quotes">
            <Card>
              <CardHeader>
                <CardTitle>Quote Requests</CardTitle>
              </CardHeader>
              <CardContent>
                {quotes.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No quote requests yet</p>
                ) : (
                  <div className="space-y-3">
                    {quotes.map((quote) => (
                      <div key={quote.id} className="p-4 border rounded-lg">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-medium">{quote.work_type}</p>
                            <p className="text-sm text-gray-500">{quote.customer_name} • {quote.customer_phone}</p>
                            <p className="text-sm text-gray-600 mt-2">{quote.work_description}</p>
                            {quote.budget_range && (
                              <p className="text-sm text-purple-600 mt-1">Budget: {quote.budget_range}</p>
                            )}
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <Badge variant={quote.status === 'pending' ? 'secondary' : quote.status === 'accepted' ? 'default' : 'outline'}>
                              {quote.status}
                            </Badge>
                            {quote.has_milestones && (
                              <Badge variant="outline" className="text-xs bg-purple-50">
                                Milestones: {quote.completed_milestones || 0}/{quote.total_milestones || 0}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        {quote.status === 'pending' && (
                          <div className="mt-4 pt-4 border-t">
                            <Button 
                              onClick={() => {
                                setSelectedQuote(quote);
                                setShowQuoteModal(true);
                              }}
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Send Quote
                            </Button>
                          </div>
                        )}
                        
                        {quote.status === 'quoted' && (
                          <div className="mt-4 pt-4 border-t bg-green-50 p-3 rounded">
                            <p className="text-sm"><strong>Your Quote:</strong> {provider?.currency} {quote.quoted_price?.toLocaleString()}</p>
                            <p className="text-sm"><strong>Timeline:</strong> {quote.quoted_timeline}</p>
                          </div>
                        )}
                        
                        {quote.status === 'accepted' && (
                          <div className="mt-4 pt-4 border-t space-y-3">
                            <div className="bg-green-50 p-3 rounded">
                              <p className="text-sm text-green-700 font-medium">✓ Quote accepted by customer</p>
                              <p className="text-sm"><strong>Agreed Price:</strong> {provider?.currency} {quote.quoted_price?.toLocaleString()}</p>
                            </div>
                            <div className="flex gap-2">
                              <Button 
                                variant="outline"
                                onClick={() => {
                                  setSelectedQuoteForMilestone(quote);
                                  setShowMilestoneModal(true);
                                }}
                              >
                                <Eye className="h-4 w-4 mr-2" />
                                {quote.has_milestones ? 'View Milestones' : 'Set Up Milestones'}
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Milestones Tab */}
          <TabsContent value="milestones">
            <div className="space-y-4">
              <Card>
                <CardHeader>
                  <CardTitle>Active Milestones</CardTitle>
                  <CardDescription>Track progress on your quote-based projects</CardDescription>
                </CardHeader>
                <CardContent>
                  {quotes.filter(q => q.status === 'accepted').length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Calendar className="h-12 w-12 mx-auto mb-3 text-gray-400" />
                      <p>No active projects with milestones</p>
                      <p className="text-sm">Accepted quotes will appear here for milestone tracking</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {quotes.filter(q => q.status === 'accepted').map((quote) => (
                        <div key={quote.id} className="border rounded-lg p-4">
                          <div className="flex items-center justify-between mb-4">
                            <div>
                              <h3 className="font-semibold">{quote.work_type}</h3>
                              <p className="text-sm text-gray-500">{quote.customer_name}</p>
                            </div>
                            <Badge className="bg-purple-100 text-purple-700">
                              {provider?.currency} {quote.quoted_price?.toLocaleString()}
                            </Badge>
                          </div>
                          <MilestoneTracker 
                            quoteId={quote.id}
                            providerId={providerId}
                            isProvider={true}
                            onMilestoneUpdate={fetchDashboardData}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Provider's Disputes */}
              {disputes.length > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <AlertTriangle className="h-5 w-5 text-orange-500" />
                      Active Disputes
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {disputes.filter(d => d.status !== 'resolved' && d.status !== 'closed').map((dispute) => (
                        <div key={dispute.id} className="p-4 border rounded-lg border-orange-200 bg-orange-50">
                          <div className="flex items-start justify-between">
                            <div>
                              <p className="font-medium">{dispute.subject}</p>
                              <p className="text-sm text-gray-600">{dispute.description}</p>
                              <p className="text-xs text-gray-500 mt-2">
                                Raised by: {dispute.customer_name} • {new Date(dispute.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={
                              dispute.status === 'open' ? 'bg-red-100 text-red-700' :
                              dispute.status === 'under_review' ? 'bg-blue-100 text-blue-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {dispute.status.replace('_', ' ')}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Delivery Tab */}
          <TabsContent value="delivery">
            <ProviderDeliveryManagement />
          </TabsContent>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <ProviderCalendarSetup 
              providerId={providerId}
              category={provider?.categories?.[0] || 'default'}
            />
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Your Services</CardTitle>
                  <CardDescription>Manage your service offerings and pricing</CardDescription>
                </div>
                <Button onClick={() => setShowServiceModal(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Add Service
                </Button>
              </CardHeader>
              <CardContent>
                {provider?.fixed_price_services?.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No services added yet</p>
                ) : (
                  <div className="space-y-3">
                    {provider?.fixed_price_services?.map((service) => (
                      <div key={service.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{service.name}</p>
                          {service.description && (
                            <p className="text-sm text-gray-500">{service.description}</p>
                          )}
                          {service.duration && (
                            <p className="text-xs text-gray-400 mt-1">Duration: {service.duration}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-4">
                          <p className="font-bold text-purple-600">
                            {provider?.currency} {service.price?.toLocaleString()}
                          </p>
                          <Button 
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => removeService(service.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Verification Tab */}
          <TabsContent value="verification">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Verification Status
                </CardTitle>
                <CardDescription>
                  Upload documents to verify your account and unlock all features
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Current Status */}
                <div className="p-4 border rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="font-medium">Verification Status</p>
                      <p className="text-sm text-gray-500">Level {provider?.verification_level || 0}</p>
                    </div>
                    <Badge className={statusBadge?.color} variant="outline">
                      {statusBadge?.label}
                    </Badge>
                  </div>
                  {provider?.verification_notes && (
                    <p className="text-sm text-gray-600 mt-2 p-2 bg-gray-50 rounded">
                      {provider.verification_notes}
                    </p>
                  )}
                </div>

                {/* Upload Documents */}
                <div>
                  <h3 className="font-medium mb-4">Upload Verification Documents</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* ID Document */}
                    <div className="p-4 border-2 border-dashed rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <span className="font-medium">ID Document</span>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">NIC, Passport, or Driving License</p>
                      <input
                        type="file"
                        id="id-doc"
                        accept="image/*,.pdf"
                        className="hidden"
                        onChange={(e) => uploadDocument(e, 'id_document')}
                        disabled={uploading}
                      />
                      <label htmlFor="id-doc">
                        <Button variant="outline" className="w-full" disabled={uploading} asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            {uploading ? 'Uploading...' : 'Upload'}
                          </span>
                        </Button>
                      </label>
                    </div>

                    {/* Business Registration */}
                    <div className="p-4 border-2 border-dashed rounded-lg">
                      <div className="flex items-center gap-3 mb-2">
                        <FileText className="h-5 w-5 text-gray-400" />
                        <span className="font-medium">Business Registration</span>
                      </div>
                      <p className="text-sm text-gray-500 mb-3">BR Certificate (if applicable)</p>
                      <input
                        type="file"
                        id="br-doc"
                        accept="image/*,.pdf"
                        className="hidden"
                        onChange={(e) => uploadDocument(e, 'business_registration')}
                        disabled={uploading}
                      />
                      <label htmlFor="br-doc">
                        <Button variant="outline" className="w-full" disabled={uploading} asChild>
                          <span>
                            <Upload className="h-4 w-4 mr-2" />
                            {uploading ? 'Uploading...' : 'Upload'}
                          </span>
                        </Button>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Uploaded Documents */}
                {provider?.verification_documents?.length > 0 && (
                  <div>
                    <h3 className="font-medium mb-4">Uploaded Documents</h3>
                    <div className="space-y-2">
                      {provider.verification_documents.map((doc, index) => (
                        <div key={index} className="flex items-center justify-between p-3 border rounded-lg">
                          <div className="flex items-center gap-3">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="font-medium text-sm">{doc.type.replace('_', ' ')}</p>
                              <p className="text-xs text-gray-500">
                                {new Date(doc.uploaded_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <Badge variant={doc.status === 'approved' ? 'default' : 'secondary'}>
                            {doc.status}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Quote Response Modal */}
      <Dialog open={showQuoteModal} onOpenChange={setShowQuoteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Quote</DialogTitle>
          </DialogHeader>
          {selectedQuote && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedQuote.work_type}</p>
                <p className="text-sm text-gray-600">{selectedQuote.work_description}</p>
                {selectedQuote.budget_range && (
                  <p className="text-sm text-purple-600 mt-1">Customer&apos;s budget: {selectedQuote.budget_range}</p>
                )}
              </div>
              
              <div>
                <Label>Your Price ({provider?.currency})</Label>
                <Input
                  type="number"
                  value={quoteResponse.price}
                  onChange={(e) => setQuoteResponse({ ...quoteResponse, price: e.target.value })}
                  placeholder="Enter your price"
                />
              </div>
              
              <div>
                <Label>Estimated Timeline</Label>
                <Input
                  value={quoteResponse.timeline}
                  onChange={(e) => setQuoteResponse({ ...quoteResponse, timeline: e.target.value })}
                  placeholder="e.g., 2-3 weeks"
                />
              </div>
              
              <div>
                <Label>Message (Optional)</Label>
                <Textarea
                  value={quoteResponse.message}
                  onChange={(e) => setQuoteResponse({ ...quoteResponse, message: e.target.value })}
                  placeholder="Add a message for the customer..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuoteModal(false)}>
              Cancel
            </Button>
            <Button onClick={respondToQuote}>
              <Send className="h-4 w-4 mr-2" />
              Send Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Service Modal */}
      <Dialog open={showServiceModal} onOpenChange={setShowServiceModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add New Service</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Service Name *</Label>
              <Input
                value={newService.name}
                onChange={(e) => setNewService({ ...newService, name: e.target.value })}
                placeholder="e.g., Hem Dress/Skirt"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={newService.description}
                onChange={(e) => setNewService({ ...newService, description: e.target.value })}
                placeholder="Describe the service..."
                rows={2}
              />
            </div>
            
            <div>
              <Label>Price ({provider?.currency}) *</Label>
              <Input
                type="number"
                value={newService.price}
                onChange={(e) => setNewService({ ...newService, price: e.target.value })}
                placeholder="500"
              />
            </div>
            
            <div>
              <Label>Duration</Label>
              <Input
                value={newService.duration}
                onChange={(e) => setNewService({ ...newService, duration: e.target.value })}
                placeholder="e.g., 1-2 days"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowServiceModal(false)}>
              Cancel
            </Button>
            <Button onClick={addService}>
              <Plus className="h-4 w-4 mr-2" />
              Add Service
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Milestone Modal */}
      <Dialog open={showMilestoneModal} onOpenChange={setShowMilestoneModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Project Milestones</DialogTitle>
          </DialogHeader>
          {selectedQuoteForMilestone && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-50 rounded-lg">
                <p className="font-medium">{selectedQuoteForMilestone.work_type}</p>
                <p className="text-sm text-gray-600">{selectedQuoteForMilestone.customer_name}</p>
                <p className="text-sm text-purple-600 font-medium mt-1">
                  {provider?.currency} {selectedQuoteForMilestone.quoted_price?.toLocaleString()}
                </p>
              </div>
              
              <MilestoneTracker 
                quoteId={selectedQuoteForMilestone.id}
                providerId={providerId}
                isProvider={true}
                onMilestoneUpdate={fetchDashboardData}
              />
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMilestoneModal(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Modal - for viewing provider's disputes */}
      <DisputeModal
        open={showDisputeModal}
        onClose={() => {
          setShowDisputeModal(false);
          setDisputeBookingId(null);
          setDisputeQuoteId(null);
        }}
        bookingId={disputeBookingId}
        quoteId={disputeQuoteId}
        providerId={providerId}
        providerName={provider?.business_name || providerName}
        onSubmit={() => {
          fetchDashboardData();
          setShowDisputeModal(false);
        }}
      />
    </div>
  );
};

export default ProviderDashboard;
